<template>
  <div class="event-options">
    <div v-for="option in eventOptions" :key="option.type + option.value" class="event-option-chip">
      <span v-if="option.icon" class="icon">
        <img :src="option.icon" :alt="option.alt" width="20" height="20" />
      </span>
      <span class="label">{{ option.value }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18 } from '@/composables/useI18'
import type { ApiEvent } from '@/types/ApiEvent'

import addressVenueIcon from 'assets/icons/icon-address-venue.svg'
import seatingStyleIcon from 'assets/icons/icon-seating-style.svg'
import foodProviderIcon from 'assets/icons/icon-food-provider.svg'
import drinkProviderIcon from 'assets/icons/icon-drink-provider.svg'
import eventOptionalStyleIcon from 'assets/icons/icon-event-optional-style.svg'

const MAX_DISPLAY_OPTIONS = 6

const { useI18: t } = useI18()

const props = defineProps<{ event: ApiEvent }>()

interface EventOption {
  type: string
  value: string
  icon: string
  alt: string
}

const addressVenues = computed(() => {
  if ('access_place' in props.event.detail && props.event.detail.access_place) {
    const addressVenue = props.event.detail.access_place.address_venue
    const availableEventVenues = ['chartered_venue', 'outdoor', 'multiple_places']

    if (addressVenue && availableEventVenues.includes(addressVenue)) {
      return [t(`event.attributes.address_venue.${addressVenue}`)]
    } else {
      return []
    }
  } else return []
})

const seatingStyles = computed(() => {
  const seatingStyle = props.event.seating_style
  const seatingStyleOptions = ['sit_down', 'stand_up', 'both']
  if (seatingStyle && seatingStyleOptions.includes(seatingStyle)) {
    return [t(`event.attributes.seating_style.${seatingStyle}`)]
  } else return []
})

const foodProviders = computed(() => {
  const foodProvider = props.event.food_provider
  const availableFoodOptions = ['course', 'single', 'buffet', 'snack']
  if (foodProvider && availableFoodOptions.includes(foodProvider)) {
    return [t(`event.attributes.food_provider.${foodProvider}`)]
  } else return []
})

const drinkProviders = computed(() => {
  const drinkProviderDetail = props.event.drink_provider_detail
  const drinkServiceOptions = ['アルコール提供あり', 'ソフトドリンクのみ', '飲み放題', 'キャッシュオン']
  if (Array.isArray(drinkProviderDetail)) {
    return drinkProviderDetail.filter(provider => drinkServiceOptions.includes(provider))
  } else return []
})

const eventOptionalStyles = computed(() => {
  const eventOptionalStyleContainers = props.event.event_optional_styles
  const availableOptions = ['oneonone', 'has_freetime', 'has_seat_changing', 'has_dresscode', 'has_recreation'] as const

  if (Array.isArray(eventOptionalStyleContainers) && eventOptionalStyleContainers.length > 0) {
    return availableOptions.filter(style => eventOptionalStyleContainers.map(item => item.event_style).includes(style))
  } else return []
})

const eventOptions = computed<EventOption[]>(() => {
  const options: EventOption[] = []

  addressVenues.value.forEach(value => {
    options.push({
      type: 'address_venue',
      value,
      icon: addressVenueIcon,
      alt: '会場'
    })
  })

  seatingStyles.value.forEach(value => {
    options.push({
      type: 'seating_style',
      value,
      icon: seatingStyleIcon,
      alt: '形式'
    })
  })

  foodProviders.value.forEach(value => {
    options.push({
      type: 'food_provider',
      value,
      icon: foodProviderIcon,
      alt: 'フード'
    })
  })

  drinkProviders.value.forEach(value => {
    options.push({
      type: 'drink_provider',
      value,
      icon: drinkProviderIcon,
      alt: 'ドリンク'
    })
  })

  eventOptionalStyles.value.forEach(style => {
    options.push({
      type: 'event_optional_style',
      value: t(`event.attributes.event_optional_style.${style}`),
      icon: eventOptionalStyleIcon,
      alt: 'スタイル'
    })
  })

  return options.slice(0, MAX_DISPLAY_OPTIONS)
})
</script>

<style lang="scss" scoped>
.event-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;

  @include pc {
    padding-left: 8px;
    padding-right: 8px;
  }
}

.event-option-chip {
  display: flex;
  align-items: center;
  background: $gray-f1;
  border-radius: 50px;
  padding: 2px 8px;
  font-size: 1.4rem;
  color: $black-1a;
  gap: 4px;
}

.event-option-chip .icon img {
  display: block;
}
</style>
